import React, { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { reApi, errMsg } from "@/lib/residentApi";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import { SubjectCards, ScoreForm } from "@/components/portal/lp/ged/Baseline";

function Toggle({ d, isSuper, onSet }) {
  return (
    <label data-testid="ged-enabled-toggle" className={`flex items-center gap-3 border border-white/10 p-3 ${isSuper ? "cursor-pointer" : "opacity-70"}`}>
      <input type="checkbox" checked={d.enabled} disabled={!isSuper} onChange={(e) => onSet(e.target.checked)} data-testid="ged-enabled-checkbox" className="h-5 w-5 accent-[#d4a017]" />
      <span className="text-sm text-nex-silver font-semibold">GED Pathway enabled</span>
      <span className="text-xs text-nex-muted">{d.enabled ? "Shows on their PACT Life Pass" : "Hidden from their PACT Life Pass"}{!isSuper ? " · Super Admin only" : ""}</span>
    </label>
  );
}

function Milestones({ d, onMark }) {
  return (
    <div className="space-y-1.5" data-testid="ged-staff-steps">
      {d.steps.map((s) => (
        <div key={s.n} className="flex items-center gap-3 border border-white/10 p-2.5">
          <span className={`h-7 w-7 grid place-items-center rounded-full text-xs font-bold ${s.done ? "bg-emerald-500 text-black" : "bg-white/10 text-nex-muted"}`}>{s.done ? <Check className="h-4 w-4" /> : s.n}</span>
          <span className="flex-1 text-sm text-nex-silver">{s.label}{s.auto ? <span className="text-xs text-emerald-300"> · done in the app</span> : s.marked ? <span className="text-xs text-nex-muted"> · marked by {s.marked.by?.name}</span> : null}</span>
          {!s.auto && s.n < 5 && <button type="button" onClick={() => onMark(s.n, !s.marked)} data-testid={`ged-mark-${s.n}`} className="text-[11px] border border-nex-gold/50 text-nex-gold px-2 py-1 hover:bg-nex-gold/10">{s.marked ? "Unmark" : "Mark done"}</button>}
        </div>
      ))}
    </div>
  );
}

export default function GedTab({ resident }) {
  const [d, setD] = useState(null);
  const isSuper = useIsSuper();
  const url = `/residents/${resident.id}/ged`;
  useEffect(() => { reApi.get(url).then(setD).catch((e) => toast.error(errMsg(e))); }, [url]);
  const call = (p) => p.then(setD).catch((e) => toast.error(errMsg(e)));
  if (!d) return <div className="flex items-center gap-2 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const focus = d.subjects.find((s) => s.key === d.focus);
  return (
    <div className="space-y-5 max-w-3xl" data-testid="ged-staff">
      <Toggle d={d} isSuper={isSuper} onSet={(v) => call(reApi.put(`${url}/enabled`, { done: v })).then(() => toast.success(v ? "GED Pathway turned on" : "GED Pathway turned off"))} />
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="border border-white/10 p-3" data-testid="ged-staff-focus"><div className="text-[10px] uppercase tracking-[0.2em] text-nex-muted">Current focus subject</div>
          <div className="text-lg text-nex-silver font-semibold mt-1">{focus ? `${focus.name} · ${focus.score}` : "No baseline yet"}</div></div>
        <div className="border border-white/10 p-3" data-testid="ged-staff-ready">
          <div className="text-[10px] uppercase tracking-[0.2em] text-nex-muted">Official exam</div>
          {d.ready ? <div className="text-emerald-300 font-semibold mt-1">Ready · flagged by {d.ready.by?.name} {d.ready.at?.slice(0, 10)}</div>
            : <div className="text-sm text-nex-muted mt-1">{d.ready_eligible ? "Has a 145+ score" : "Needs a 145+ practice score"}</div>}
          <button type="button" onClick={() => call(reApi.put(`${url}/ready`, { done: !d.ready }))} disabled={!d.ready && !d.ready_eligible} data-testid="ged-ready-btn"
            className="mt-2 text-xs border border-emerald-400/60 text-emerald-300 px-2.5 py-1 hover:bg-emerald-500/10 disabled:opacity-40">{d.ready ? "Remove ready flag" : "Flag ready for official GED exam"}</button>
        </div>
      </div>
      <div><div className="text-xs uppercase tracking-[0.2em] text-nex-gold mb-2">Diagnostic baseline</div><SubjectCards subjects={d.subjects} focus={d.focus} light /></div>
      <ScoreForm subjects={d.subjects} onSave={(b) => reApi.post(`${url}/scores`, b).then(setD)} testid="ged-staff-score" />
      <div><div className="text-xs uppercase tracking-[0.2em] text-nex-gold mb-2">Roadmap milestones</div><Milestones d={d} onMark={(n, v) => call(reApi.put(`${url}/milestones/${n}`, { done: v }))} /></div>
      <div className="text-xs text-nex-muted" data-testid="ged-staff-lessons">Lessons done: {Object.values(d.lessons).flat().filter((l) => l.done).length}/12 · Launchpad {Object.values(d.launchpad).filter(Boolean).length}/3</div>
    </div>
  );
}
